import React, { useEffect, useState } from "react";

//UI Material
import {
  Button,
  Grid,
  TextField,
  Stack,
  Alert,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";

//Local components
import ProductCard from "../Components/new-order-page/ProductCard";
import ModalWindow from "../Components/Modal";
import Selector from "../Components/basics/Selector";
import SetClientDrawer from "../Components/drawers/SetClientDrawer";
import DataLoader from "../Components/basics/DataLoader";
import ErrorBox from "../Components/ErrorBox";
import NexaCutoverNotice from "../Components/NexaCutoverNotice";

//Helpers
import { fetchWithTokenJSONPost } from "../Helpers/network";
import { productTypes } from "../Dictionaries/products";

//UI-X Material
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

//Material Icons
import { Add, RemoveCircle } from "@mui/icons-material";


const clientTypes = ["Física", "Jurídica", "Otra", "Todos"];

export default function NewOrder() {
  // ==========> Component States <==========
  //State for setting error when could not get data
  const [dataError, setDataError] = useState(false);
  //State for client
  const [client, setClient] = useState("");
  //State for dueDate
  const [dueDate, setDueDate] = useState(new Date());
  //State for orders
  const [cart, setCart] = useState([]);
  //State for open modal
  const [modalOpen, setModalOpen] = useState(false);
  //State for modal description
  const [modalDescription, setModalDescription] = useState("");
  //State for modal text
  const [modalText, setModalText] = useState("");
  //State for loading data from db
  const [isLoading, setIsLoading] = useState(true);
  //State for new order creation loader
  const [isLoadingNewOrder, setIsLoadingNewOrder] = useState(false);
  //State for the selected client
  const [clientForOrder, setClientForOrder] = useState(0);
  //State for clients list
  const [clientsList, setClientsList] = useState([]);
  //State for products list
  const [productsList, setProductsList] = useState([]);
  //State for open add-client drawer
  const [openClientDrawer, setOpenClientDrawer] = useState(false);
  //State for selected client type by id
  const [selectedClientType, setSelectedClientType] = useState(0);
  //State for product type
  const [selectedProductType, setSelectedProductType] = useState(0);

  const productTypeOptions = ["ALL", ...productTypes.map((p) => p.value)];

  //Function to filter clients according to selectedType
  const filteredClients = () => {
    switch (selectedClientType) {
      case 0:
      case 1:
      case 2:
        return clientsList.filter(
          (client) => client.type === selectedClientType
        );
      default:
        return clientsList;
    }
  };

  // ==========> Component Functions <==========
  //Function to set dueDate value
  const handleDueDate = (_event) => {
    setDueDate(_event.toISOString());
  };

  //Function to get clients list
  const getClientsList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(`/clients/get`, {
        howMany: 5000,
      });
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setClientsList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(true);
    }
  };

  //Function to get products list
  const getProductsList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/products/get-products-allowed-for-orders`
      );
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setProductsList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(true);
    }
  };

  //Function to generate cart items
  const generateCartItems = () => {
    return cart.map((order) => (
      <Stack
        key={`${order.numberOfProducts} ${order.aditionalData.productName}`}
        direction="row"
        alignItems="center"
        spacing={1}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" disabled>
            <RemoveCircle />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            {order.numberOfProducts}
          </Typography>
          <Typography variant="body2">
            {order.aditionalData.productName}
          </Typography>
        </Stack>
        <Typography variant="h6" fontWeight="bold">
          {`$${Number(order.billing.amount).toFixed(2)}`}
        </Typography>
      </Stack>
    ));
  };

  //Function to create the orders
  const createNewOrder = async () => {
    setIsLoadingNewOrder(true);
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/orders/create-multiple-orders`,
        {
          client: client,
          dueDate: new Date(dueDate).toISOString(),
          priority: false,
          products: cart,
          clientId: filteredClients()[clientForOrder].id,
        }
      );
      const jsonResponse = await requestResponse.json();
      if (jsonResponse.status === "ok") {
        setModalDescription("Orden registrada exitosamente");
        setModalText(jsonResponse.description);
        window.location.reload();
      } else {
        setModalDescription("No se pudo realizar la acción.");
        setModalText(jsonResponse.description);
      }
    } catch (error) {
      setModalDescription("No se pudo realizar la acción.");
      setModalText("Por favor intente nuevamente más tarde.");
    }
    setModalOpen(true);
    setIsLoadingNewOrder(false);
  };

  // ==========> Component useEffect <==========
  useEffect(() => {
    getClientsList();
    getProductsList();
    setIsLoading(false);
  }, []);

  const displayedProducts =
    selectedProductType === 0
      ? productsList
      : productsList.filter((prod) => {
          const typeName = productTypeOptions[selectedProductType];
          return prod.type === typeName;
        });

  // ==========> Component <==========
  if (isLoading) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <NexaCutoverNotice />
        </Grid>
        <Grid item xs={12}>
          <DataLoader />
        </Grid>
      </Grid>
    );
  }

  return dataError ? (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <NexaCutoverNotice />
      </Grid>
      <Grid item xs={12}>
        <ErrorBox />
      </Grid>
    </Grid>
  ) : (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <NexaCutoverNotice />
      </Grid>
      <SetClientDrawer
        open={openClientDrawer}
        setOpen={setOpenClientDrawer}
        getData={getClientsList}
      />
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight="bold">
          Registrar Nueva Orden
        </Typography>
      </Grid>

      <Grid item xs={12} md={9}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight="bold">
              1. Información del cliente que solicita la orden
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack>
              <Typography variant="body2">
                Seleccione el tipo de identificación del cliente
              </Typography>
              <Selector
                content={clientTypes}
                value={selectedClientType}
                handleChange={(e) => {
                  setClientForOrder(0);
                  setSelectedClientType(e.target.value);
                }}
                width="100%"
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack>
              <Typography variant="body2">
                Seleccione el cliente en la lista de registrados
              </Typography>
              <Selector
                content={filteredClients().map((client) => client.name)}
                value={clientForOrder}
                handleChange={(e) => {
                  setClientForOrder(e.target.value);
                  setClient(filteredClients()[e.target.value].name);
                }}
                width="100%"
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              sx={{ color: "white" }}
              endIcon={<Add />}
              onClick={() => {
                setOpenClientDrawer(true);
              }}
            >
              Agregar nuevo cliente
            </Button>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              2. Seleccionar los productos que incluye la orden
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Stack>
              <Typography variant="body2">
                Seleccione el tipo de producto que desea agregar
              </Typography>
              <Selector
                content={productTypeOptions}
                value={selectedProductType}
                handleChange={(e) => {
                  setSelectedProductType(e.target.value);
                }}
                width="100%"
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {displayedProducts.map((product) => (
                <Grid item xs={12} md={6} key={product.productId}>
                  <ProductCard
                    product={product}
                    cart={cart}
                    setCart={setCart}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={3}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight="bold">
              3. Resumen de la orden
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#00BCCD" }}
                  >
                    Nombre del cliente
                  </Typography>
                  <Typography variant="body2">
                    {client === "" ? "Sin cliente" : client}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#00BCCD" }}
                  >
                    Fecha de entrega
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MobileDateTimePicker
                      inputFormat="dd-MM-yyyy HH:mm"
                      value={dueDate}
                      onChange={handleDueDate}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#00BCCD" }}
                  >
                    Productos seleccionados
                  </Typography>
                  {!cart.length ? (
                    <Typography variant="body2">
                      No hay productos en el carrito.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>{generateCartItems()}</Stack>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Alert severity="warning">
              Por favor asegúrese de haber agregado los productos correctamente
              antes de crear la orden de producción.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ marginTop: "10px" }}
            >
              <Button
                size="small"
                fullWidth
                variant="contained"
                color="error"
                onClick={() => {
                  setCart([]);
                }}
              >
                Vaciar carrito
              </Button>
              {isLoadingNewOrder ? (
                <CircularProgress />
              ) : (
                <Button
                  size="small"
                  variant={!cart.length ? "disabled" : "contained"}
                  fullWidth
                  color="secondary"
                  sx={{ color: "white" }}
                  onClick={() => {
                    createNewOrder();
                  }}
                >
                  Crear orden
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Grid>

      <ModalWindow
        isOpen={modalOpen}
        title={modalDescription}
        text={modalText}
        leftText="Ok"
        leftClick={() => {
          setModalOpen(false);
        }}
      />
    </Grid>
  );
}
